import { createStore } from 'vuex'
import { v4 as uuidv4 } from "uuid"
import { instance, blob_instance, fixImageDuration, reorderArray } from "./axios"


export default createStore({
  state: {
    apiStatus: {
      version: null,
      demo_mode: false
    },
    projects: [],
    files: [],
    clips: [],
    errors: [],
    uploads: [],
    exports: [],
    effects: [],
    project: null,
    current_export: { progress: 0.0 },
    effectCatalog: [],
    user: null,
    preview: {
      file: null,
      clip: null,
      start: 0.0,
      end: 1.0,
      position: 0.0,
      length: 0.0
    },
    scrollToClip: null,
    default_image_length: 30
  },

  mutations: {
    setApiStatus(state, status) {
      state.apiStatus = {
        version: status?.version || null,
        demo_mode: status?.demo_mode === true
      }
    },
    addError(state, message) {
      if (typeof(message) == "object") {
        // Loop through object keys (potentially multiple errors)
        for (const key in message) {
          let error = {'id': uuidv4(), 'message': `${key}: ${message[key]}`}
          state.errors.push(error)
        }
      } else {
        // Single error message
        let error = {'id': uuidv4(), 'message': message}
        state.errors.push(error)
      }
    },
    removeError(state, error) {
      state.errors = state.errors.filter(err => err.id != error.id )
    },
    clearErrors(state) {
      state.errors = []
    },
    setScrollToClip(state, clipObj) {
      state.scrollToClip = clipObj
    },
    setUser(state, user) {
      state.user = user
    },
    addProject(state, projectObj) {
      state.projects.push(projectObj)
    },
    setProjects(state, projects) {
      state.projects = projects
    },
    setProject(state, project) {
      state.project = project
    },
    addEffect(state, effectObj) {
      state.effects.push(effectObj)
    },
    setEffects(state, effects) {
      state.effects = effects
    },
    setEffect(state, effectObj) {
      state.effects = [
        ...state.effects = state.effects.filter(e => e.id != effectObj.id),
        effectObj
      ]
    },
    setEffectCatalog(state, effects) {
      state.effectCatalog = effects || []
    },
    updateProjectThumbnail(state, payload) {
      state.projects = [
        ...state.projects = state.projects.filter(p => p.id != payload.obj.id),
        payload.obj
      ]
    },
    deleteProject(state, project_id) {
      state.projects = state.projects.filter(p => p.id != project_id)
    },
    setFiles(state, files) {
      state.files = files
    },
    addFile(state, fileObj) {
      state.files.push(fileObj)
    },
    updateFileThumbnail(state, payload) {
      if (!state.project?.url || !Array.isArray(payload.obj.projects)) {
        return
      }
      if (payload.obj.projects.includes(state.project.url)) {
        state.files = [
          ...state.files = state.files.filter(file => file.id != payload.obj.id),
          payload.obj
        ]
      }
    },
    deleteFile(state, file_id) {
      state.files = state.files.filter(file => file.id != file_id)
    },
    addClip(state, clip) {
      state.clips.push(clip)
    },
    setClips(state, clips) {
      state.clips = clips
      if (!clips) {
        // clear latest clip if no clips
        state.scrollToClip = null
      }
    },
    setClip(state, clipObj) {
      state.clips = [
        ...state.clips = state.clips.filter(clip => clip.id != clipObj.id),
        clipObj
      ]
    },
    deleteClip(state, clip_id) {
      state.clips = state.clips.filter(clip => clip.id != clip_id)
    },
    updateClipThumbnail(state, payload) {
      if (!state.project?.url) {
        return
      }
      if (state.project.url == payload.obj.project) {
        state.clips = [
          ...state.clips = state.clips.filter(clip => clip.id != payload.obj.id),
          payload.obj
        ]
        if (payload.latest) {
          state.scrollToClip = payload.obj
        }
      }
    },
    setPreview(state, payload) {
      state.preview.start = payload.start
      state.preview.end = payload.end
      if (state.preview.file) {
        state.preview.length = (state.preview.end - state.preview.start) *
            fixImageDuration(state.preview.file.json.duration)
      }
    },
    setPreviewPosition(state, position) {
      state.preview.position = position
    },
    setPreviewFile(state, file) {
      state.preview.file = file
      state.preview.clip = null
      state.preview.start = 0.0
      state.preview.end = 1.0
      state.preview.position = 0.0
      if (state.preview.file) {
        state.preview.length = (state.preview.end - state.preview.start) *
            fixImageDuration(state.preview.file.json.duration)
      }
    },
    setPreviewClip(state, clipObj) {
      state.preview.clip = clipObj
      if (clipObj && clipObj.fileObj) {
        state.preview.start = clipObj.start / fixImageDuration(clipObj.fileObj.json.duration)
        state.preview.end = clipObj.end / fixImageDuration(clipObj.fileObj.json.duration)
        state.preview.position = clipObj.start / fixImageDuration(clipObj.fileObj.json.duration)
        if (state.preview.clip.fileObj) {
          state.preview.file = state.preview.clip.fileObj
        }
      } else {
        state.preview.file = null
        state.preview.start = 0.0
        state.preview.end = 1.0
        state.preview.position = 0.0
      }

      if (state.preview.file) {
        state.preview.length = (state.preview.end - state.preview.start) *
            fixImageDuration(state.preview.file.json.duration)
      }
    },
    addUpload(state, upload) {
      state.uploads.push(upload)
    },
    updateUpload(state, upload) {
      for (const up of state.uploads) {
        if (up.id == upload.id) {
          up.progress = upload.progress
          break
        }
      }
    },
    removeUpload(state, uploadID) {
      state.uploads = state.uploads.filter(up => up.id != uploadID )
    },
    setExports(state, exports) {
      state.exports = exports
      if (state.exports.length == 0) {
        state.current_export = { progress: 0.0 }
      }
    },
    setExport(state, exportObj) {
      state.exports = [
        ...state.exports = state.exports.filter(e => e.id != exportObj.id),
        exportObj
      ]
      state.current_export = exportObj
    },
  },
  getters: {
    isAuthenticated: state => !!state.user,
    totalClipDuration: state => {
      return state.clips.map(clip => clip.end - clip.start).reduce((prev, curr) => prev + curr, 0.0)
    },
    thumbnailedClips: state => {
      return state.clips.filter(clip => clip.thumbnail).sort((a, b) => a.position - b.position)
    },
    thumbnailedProjects: state => {
      return state.projects.filter(project => project.thumbnail).sort((a, b) => b.id - a.id)
    },
    activeExports: state => {
      return state.exports.filter(e => e.status == "preparing" ||
          e.status == "pending" ||
          e.status == "in-progress" ||
          e.status == "finalizing").sort((a, b) => b.id - a.id)
    }
  },
  actions: {
    async loadApiStatus({commit}) {
      try {
        const response = await instance.get('/info/status/')
        commit('setApiStatus', response.data)
      } catch (_error) {
        commit('setApiStatus', {version: null, demo_mode: false})
      }
    },
    async login({commit}, auth) {
      try {
        const response = await instance.get('/users/')
        let user_object = response.data.results[0]
        user_object.auth = auth
        commit('setUser', user_object)
      } catch(err) {
        commit('setUser', null)
        commit('addError', err.response?.data || err.message)
      }
    },
    async loadProjects({commit, dispatch}) {
      try {
        const response = await instance.get('/projects/')
        // Sort all projects, and limit to X most recent
        let sorted_projects = response.data.results.sort().reverse().slice(0, 12)
        commit('setProjects', sorted_projects)
        for (let p of response.data.results) {
          let payload = { obj: p, frame: 1 }
          dispatch('attachThumbnail', payload)
        }
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async getProject({commit}, project_id) {
      try {
        return instance.get(`projects/${project_id}/`)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async createProject({commit, dispatch}, payload) {
      try {
        const response = await instance.post('/projects/', payload)
        commit('addProject', response.data)
        let thumbnail_payload = { obj: response.data, frame: 1 }
        dispatch('attachThumbnail', thumbnail_payload)
        return response.data
      } catch(err) {
        commit('addError', err.response?.data || err.message)
        return null
      }
    },
    async loadProjectArchive({commit}, payload) {
      if (!payload || !payload.projectId || !payload.file) {
        return
      }
      try {
        const formData = new FormData()
        formData.append('archive', payload.file)
        await instance.post(`/projects/${payload.projectId}/load/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } catch(err) {
        commit('addError', err.response?.data || err.message)
        throw err
      }
    },
    async updateProject({commit}, payload) {
      try {
        await instance.patch(`${payload.url}`, payload)
        commit('setProject', payload)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async deleteProject({commit}, project_id) {
      try {
        await instance.delete(`projects/${project_id}/`)
        commit('deleteProject', project_id)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async loadFiles({commit, dispatch}, project_id) {
      try {
        const response = await instance.get(`projects/${project_id}/files/`)
        let files = response.data.results

        // Generate thumbnail for each file object
        for (let fileObj of files) {
          // fix long duration on images
          fileObj.json.duration = fixImageDuration(fileObj.json.duration)
          let payload = { obj: fileObj, frame: 1 }
          dispatch('attachThumbnail', payload)
        }
        commit('setFiles', files)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async loadClips({commit, dispatch}, project_id) {
      try {
        const response = await instance.get(`projects/${project_id}/clips/`)
        let clips = response.data.results

        // Attach file obj to clip
        for (let clipObj of clips) {
          const file_response = await instance.get(clipObj.file)
          clipObj.fileObj = file_response.data
          commit('addClip', clipObj)

          let fps = clipObj.json.reader.fps.num / clipObj.json.reader.fps.den
          let thumbnail_payload = { obj: clipObj, frame: clipObj.start * fps, latest: false }
          dispatch('attachThumbnail', thumbnail_payload)
        }
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async createClip({dispatch, commit}, payload) {
      try {
        const response = await instance.post(`${payload.project_url}clips/`, payload.data)
        let clipObj = response.data
        const file_response = await instance.get(clipObj.file)
        clipObj.fileObj = file_response.data
        commit('addClip', clipObj)

        let fps = clipObj.json.reader.fps.num / clipObj.json.reader.fps.den
        let thumbnail_payload = { obj: clipObj, frame: clipObj.start * fps, latest: true }
        await dispatch('attachThumbnail', thumbnail_payload)
        return clipObj
      } catch(err) {
        commit('addError', err.response?.data || err.message)
        return null
      }
    },
    async editClip({dispatch, commit}, payload) {
      try {
        await instance.patch(`${payload.data.url}`, payload.data)
        commit('setClip', payload.data)
        let fps = payload.data.json.reader.fps.num / payload.data.json.reader.fps.den
        let thumbnail_payload = { obj: payload.data, frame: payload.data.start * fps, latest: payload.latest }
        if (payload.thumbnail) {
          dispatch('attachThumbnail', thumbnail_payload)
        } else if (payload.latest) {
          commit('setScrollToClip', payload.data)
        }
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async moveClip({dispatch, getters}, payload) {
      let local_clips = [...getters.thumbnailedClips]
      if (!Array.isArray(local_clips) || local_clips.length === 0) {
        return
      }
      if (payload.current === payload.dest || payload.current === undefined || payload.dest === undefined) {
        return
      }
      let reordered_clips = reorderArray(local_clips, payload.current, payload.dest)
      let pos = 0.0
      let updates = []
      const maxIndex = reordered_clips.length - 1
      const affectedStart = Math.max(0, Math.min(payload.current, payload.dest))
      const affectedEnd = Math.min(maxIndex, Math.max(payload.current, payload.dest))
      for (let i = 0; i < reordered_clips.length; i++) {
        let clip = reordered_clips[i]
        let newPosition = pos
        if (i >= affectedStart && i <= affectedEnd && clip.position !== newPosition) {
          clip.position = newPosition
          let edit_payload = { data: clip, latest: clip.id == payload.clip.id, thumbnail: false }
          updates.push(edit_payload)
        }
        pos += (clip.end - clip.start)
      }
      for (let edit_payload of updates) {
        await dispatch('editClip', edit_payload)
      }
      await dispatch('normalizeClipPositions')
    },
    async deleteClip({commit, dispatch}, clip_id) {
      try {
        await instance.delete(`clips/${clip_id}/`)
        commit('deleteClip', clip_id)
        await dispatch('normalizeClipPositions')
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async deleteFile({commit, dispatch, state}, file_id) {
      const normalizedFileId = typeof file_id === 'number' ? file_id : Number(file_id)
      const relatedClips = state.clips.filter(clip => {
        let clipFileId = clip?.fileObj?.id
        if (!clipFileId && clip?.file) {
          if (typeof clip.file === 'string') {
            const parts = clip.file.split('/').filter(Boolean)
            clipFileId = Number(parts[parts.length - 1])
          } else if (typeof clip.file === 'number') {
            clipFileId = clip.file
          }
        }
        return clipFileId === normalizedFileId
      })

      for (const clip of relatedClips) {
        await dispatch('deleteClip', clip.id)
      }

      try {
        await instance.delete(`files/${file_id}/`)
        commit('deleteFile', normalizedFileId)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async createFile({commit, dispatch}, payload) {
      let uploadID = uuidv4()
      let uploadObject = { 'id': uploadID, progress: 0 }
      commit('addUpload', uploadObject)

      const config = {
        onUploadProgress: progressEvent => {
          let progress = (progressEvent.loaded / progressEvent.total) * 100;
          let uploadObject = { 'id': uploadID, progress }
          commit('updateUpload', uploadObject)
        }
      }

      try {
        let response = await instance.post(`${payload.project_url}files/`, payload.data, config)
        let fileObj = response.data
        // fix long duration on images
        fileObj.json.duration = fixImageDuration(fileObj.json.duration)
        commit('removeUpload', uploadID)

        let thumbnail_payload = { obj: fileObj, frame: 1 }
        dispatch('attachThumbnail', thumbnail_payload)
      } catch(err) {
        commit('removeUpload', uploadID)
        commit('addError', err.response?.data || err.message)
      }
    },
    async attachThumbnail({commit}, payload) {
      let Obj = payload.obj
      if (!Obj) {
        console.warn('attachThumbnail: missing object data', payload)
        return
      }
      let objectType = null
      if (Obj.file) {
        // Clip passed in
        Obj = Obj.fileObj
        objectType = 'clip'
      } else if (Obj.media) {
        // File passed in
        objectType = 'file'
      } else {
        // Project passed in
        objectType = 'project'
      }
      if (!Obj) {
        console.warn('attachThumbnail: missing nested object data', payload)
        return
      }
      if (!Obj.url) {
        console.warn('attachThumbnail: object missing url', payload)
        return
      }
      let sessionKey = `${objectType}-${Obj.id}-frame${payload.frame}`

      if (payload.clobber) {
        // Destroy existing cached thumbnail
        sessionStorage.removeItem(sessionKey)
      }

      let data = {
        "frame_number": payload.frame,
        "width": 480,
        "height": 270,
        "image_format": "JPEG",
        "image_quality": 100
      }
      try {
        let thumbnailUrl = sessionStorage.getItem(sessionKey)
        if (thumbnailUrl) {
          try {
            // Verify blob
            await blob_instance.get(thumbnailUrl)
          } catch(err) {
            // Failed to find blob
            thumbnailUrl = null
          }
        }
        if (!thumbnailUrl) {
          const response = await instance.post(`${Obj.url}thumbnail/`, data, { responseType: 'arraybuffer' })
          let blob = new Blob(
              [response.data],
              { type: response.headers['content-type'] }
          )
          thumbnailUrl = URL.createObjectURL(blob)
          sessionStorage.setItem(sessionKey, thumbnailUrl)
        }

        // Append thumbnail attribute to each file object
        payload.obj.thumbnail = thumbnailUrl
        if (objectType == 'file') {
          commit('updateFileThumbnail', payload)
        } else if (objectType == 'clip') {
          commit('updateClipThumbnail', payload)
        } else {
          commit('updateProjectThumbnail', payload)
        }

      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async createExport({commit}, payload) {
      try {
        const response = await instance.post('/exports/', payload)
        commit('setExport', response.data)
        return response.data
      } catch(err) {
        commit('addError', err.response?.data || err.message)
        throw err
      }
    },
    async loadExports({commit}, project_id) {
      try {
        const proj_response = await instance.get(`projects/${project_id}/`)
        const sorted_export_urls = proj_response.data.exports.sort().reverse()
        if (sorted_export_urls.length > 0) {
          // Only get most recent Export record
          const export_response = await instance.get(sorted_export_urls[0])
          commit('setExport', export_response.data)
        }
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async checkExportProgress({commit}, exportObj) {
      try {
        const export_response = await instance.get(exportObj.url)
        commit('setExport', export_response.data)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async loadEffects({commit}, project_id) {
      try {
        const effects_response = await instance.get(`projects/${project_id}/effects/`)
        commit('setEffects', effects_response.data.results)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async loadEffectCatalog({state, commit}) {
      if (state.effectCatalog && state.effectCatalog.length > 0) {
        return state.effectCatalog
      }
      try {
        const response = await instance.get('/info/effects/')
        const data = response.data && response.data.results ? response.data.results : response.data
        commit('setEffectCatalog', data || [])
        return data
      } catch(err) {
        commit('addError', err.response?.data || err.message)
        return []
      }
    },
    async createEffect({commit}, payload) {
      try {
        const response = await instance.post('/effects/', payload)
        commit('addEffect', response.data)
      } catch(err) {
        commit('addError', err.response?.data || err.message)
      }
    },
    async updateEffect({commit}, payload) {
      try {
        await instance.patch(payload.url, payload)
        commit('setEffect', payload)
      } catch(err) {
        console.log(err)
        commit('addError', err.response?.data || err.message)
      }
    },
    async normalizeClipPositions({getters, dispatch}) {
      const clips = [...getters.thumbnailedClips]
      if (!Array.isArray(clips) || clips.length === 0) {
        return
      }
      let position = 0
      const updates = []
      for (let clip of clips) {
        const duration = Math.max(0, (clip.end - clip.start))
        if (Math.abs((clip.position ?? 0) - position) > 0.0001) {
          clip.position = position
          updates.push({ data: clip, latest: false, thumbnail: false })
        }
        position += duration
      }
      for (let payload of updates) {
        await dispatch('editClip', payload)
      }
    },
  },
  modules: {
  }
})
